// Agent collections — a sub-page of the Config section's Agents folder
// (DEV-16997). A collection is a named GROUP OF AGENTS; a memory or skill
// bound to a collection applies to every member agent (resolved at read
// time — "sugar over bindings"). Collections are agent groups and do NOT
// apply to workflow llm-agent nodes (bind those from the node-config panel).
//
// This page owns the collection lifecycle + membership. Binding a memory or
// skill TO a collection is done from the memory/skill create form (pick the
// collection as an applicability target) or via the API; the counts here link
// the two.
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AgentMultiSelect } from '../../components/common/AgentMultiSelect';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import {
  useAddCollectionMembers,
  useCollection,
  useCollectionList,
  useCreateCollection,
  useDeleteCollection,
  useRemoveCollectionMember,
  useRenameCollection,
  type AgentCollectionSummary,
} from '../../lib/agent-collection';

export const Route = createFileRoute('/agents/collections')({ component: CollectionsPage });

function CollectionsPage() {
  const listQuery = useCollectionList();
  const remove = useDeleteCollection();
  const [editing, setEditing] = useState<AgentCollectionSummary | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AgentCollectionSummary | null>(null);

  const cols: IColumn<AgentCollectionSummary>[] = [
    { key: 'name', header: 'Name', cell: (c) => <span className="t-strong">{c.name}</span> },
    { key: 'members', header: 'Agents', numeric: true, cell: (c) => c.memberCount },
    { key: 'memories', header: 'Memories', numeric: true, cell: (c) => c.memoryCount },
    { key: 'skills', header: 'Skills', numeric: true, cell: (c) => c.skillCount },
    {
      key: 'actions',
      header: '',
      width: 80,
      cell: (c) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDelete(c);
          }}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Collections"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setEditing('new')}
          >
            New collection
          </button>
        }
      />
      <Table<AgentCollectionSummary>
        rows={listQuery.data}
        columns={cols}
        rowKey={(c) => c.id}
        isLoading={listQuery.isLoading}
        error={(listQuery.error as Error | null) ?? null}
        emptyTitle="No collections yet"
        emptyDescription="Group agents into a collection, then bind a memory or skill to the whole group — new members inherit it automatically."
        onRowClick={(c) => setEditing(c)}
      />
      {editing !== null ? (
        <CollectionModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      ) : null}
      <Modal
        open={confirmDelete !== null}
        onClose={() => {
          if (!remove.isPending) setConfirmDelete(null);
        }}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : ''}
        width="min(480px, 92vw)"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            The collection is removed and any memory/skill bound to it stops reaching its members on
            their next resolve. The memories, skills, and agents themselves are untouched.
          </p>
          {remove.error ? (
            <div className="field-error">{(remove.error as Error).message}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setConfirmDelete(null)}
              disabled={remove.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (!confirmDelete) return;
                remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete collection'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function CollectionModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: AgentCollectionSummary | 'new';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = editing === 'new';
  const detailQuery = useCollection(isNew ? null : editing.id);
  const create = useCreateCollection();
  const rename = useRenameCollection();
  const addMembers = useAddCollectionMembers();
  const removeMember = useRemoveCollectionMember();

  const [name, setName] = useState(isNew ? '' : editing.name);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed the member picker from the fetched detail (edit mode).
  const originalMembers = detailQuery.data?.agentIds;
  useEffect(() => {
    if (originalMembers) setMemberIds(originalMembers);
  }, [originalMembers]);

  const canSave = name.trim().length > 0 && !saving;
  const loadingDetail = !isNew && detailQuery.isLoading;

  async function save() {
    if (!canSave) return;
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await create.mutateAsync({ name: name.trim(), agentIds: memberIds });
      } else {
        const id = editing.id;
        if (name.trim() !== editing.name) {
          await rename.mutateAsync({ id, name: name.trim() });
        }
        const before = new Set(originalMembers ?? []);
        const after = new Set(memberIds);
        const added = memberIds.filter((a) => !before.has(a));
        const removed = [...before].filter((a) => !after.has(a));
        if (added.length > 0) {
          await addMembers.mutateAsync({ id, args: { agentIds: added } });
        }
        for (const agentId of removed) {
          await removeMember.mutateAsync({ id, args: { agentId } });
        }
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const bindingSummary = useMemo(() => {
    if (isNew || !detailQuery.data) return null;
    const { memoryIds, skillIds } = detailQuery.data;
    return { memories: memoryIds.length, skills: skillIds.length };
  }, [isNew, detailQuery.data]);

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isNew ? 'New collection' : `Edit "${editing.name}"`}
      width="min(560px, 92vw)"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span className="t-muted t-xs">Name</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Triage agents"
            autoFocus={true}
          />
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span className="t-muted t-xs">Member agents</span>
          {loadingDetail ? (
            <span className="t-muted t-xs">Loading members…</span>
          ) : (
            <AgentMultiSelect selectedIds={memberIds} onChange={setMemberIds} />
          )}
          <span className="t-muted t-xs">
            A memory or skill bound to this collection applies to every member — add an agent and it
            inherits the collection&rsquo;s memories/skills automatically.
          </span>
        </div>

        {bindingSummary ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }} className="t-xs">
            Bound to this collection: {bindingSummary.memories} memor
            {bindingSummary.memories === 1 ? 'y' : 'ies'} · {bindingSummary.skills} skill
            {bindingSummary.skills === 1 ? '' : 's'}. Bind more from the memory or skill create form
            (pick this collection as a target).
          </p>
        ) : null}

        {error ? <div className="field-error">{error}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={save}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : isNew ? 'Create collection' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
